import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link, withRouter, routerShape } from 'react-router';
import { withApollo } from '@apollo/react-hoc';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';

import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import Grid from '../../components/Grid';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Popup from '../../components/Popup';
import Dropdown from '../../components/Dropdown';
import Divider from '../../components/Divider';
import Menu from '../../components/Menu';
import Item from '../../components/Item';
import PageTitle from '../../components/PageTitle';
import Checkbox from '../../components/Checkbox';
import { active } from './styles.css';
import messages from './messages';
import getEventsAttendeeExportUrl from './graphql/getEventsAttendeeExportUrl';
import {
  isCaminoEventsEnabled,
  createEventObjectArray,
  getDisplayInDefaults,
  getDisplayInMessage,
} from '../../utils/college-events';

export const dropdownItemKeys = {
  editEvent: 'EDIT_EVENT',
  publishEventPast: 'PUBLISH_EVENT_PAST',
  publishEvent: 'PUBLISH_EVENT',
  unpublishEvent: 'UNPUBLISH_EVENT',
  deleteEvent: 'DELETE_EVENT',
  duplicateEvent: 'DUPLICATE_EVENT',
};

function hasEventsInTab(tab, published, drafts, expired) {
  if (tab === 'published') return !!published;
  if (tab === 'unpublished') return !!drafts;
  return !!expired;
}

export class CollegeEventsViewEventsComponent extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.object,
    data: PropTypes.object.isRequired,
    fetchAmEventsAttendeeExportAvailable: PropTypes.object,
    client: PropTypes.object,
    changeCollegeEventStatus: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    enabledFeatures: PropTypes.array,
    heAccount: PropTypes.shape({
      activeSubscriptions: PropTypes.array,
    }),
  };

  constructor(props) {
    super(props);
    const caminoEventsEnabled = isCaminoEventsEnabled(get(props, 'enabledFeatures', []));
    let eventsIn;
    let displayInDefaults = {
      displayInCheckboxes: false,
      isCaminoChecked: false,
      isNavianceChecked: false,
    };

    if (caminoEventsEnabled) {
      displayInDefaults = getDisplayInDefaults({
        activeSubscriptions: get(props, 'heAccount.activeSubscriptions', []),
      });
      eventsIn = createEventObjectArray({
        rawEvents: props.data.node.collegeEvents.edges,
        showCamino: displayInDefaults.isCaminoChecked,
        showNaviance: displayInDefaults.isNavianceChecked,
      });
    } else {
      eventsIn = createEventObjectArray({ rawEvents: props.data.node.collegeEvents.edges });
    }

    this.state = {
      events: eventsIn.events,
      published: eventsIn.published,
      drafts: eventsIn.drafts,
      expired: eventsIn.expired,
      deletingEventId: null,
      draftToast: false,
      publishedToast: false,
      unpublishedToast: false,
      toastFading: false,
      caminoEventsEnabled,
      ...displayInDefaults,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { caminoEventsEnabled, isCaminoChecked, isNavianceChecked } = this.state;
    let eventsIn;

    if (caminoEventsEnabled) {
      eventsIn = createEventObjectArray({
        rawEvents: nextProps.data.node.collegeEvents.edges,
        showCamino: isCaminoChecked,
        showNaviance: isNavianceChecked,
      });
    } else {
      eventsIn = createEventObjectArray({
        rawEvents: nextProps.data.node.collegeEvents.edges,
      });
    }
    this.showToast(eventsIn.events, false);
    this.setState({
      events: eventsIn.events,
      published: eventsIn.published,
      drafts: eventsIn.drafts,
      expired: eventsIn.expired,
    });
  }

  onSuccess = (collegeEvent) => {
    const eventData = collegeEvent.data.changeCollegeEventStatus.collegeEvent;
    const event = { ...eventData, authoredTime: Date.now() };
    this.showToast([event], true);
  };

  changeEventStatus = (id, status) => {
    this.setState({ isDeleting: false, deletingEventId: null });
    const version = (this.state.events.find((event) => event.id === id).version || /* istanbul ignore next */0);
    const collegeEvent = { id, status, version };
    const { changeCollegeEventStatus } = this.props;
    const variables = {
      input: {
        collegeEvent,
      },
    };
    changeCollegeEventStatus({ variables }).then(this.onSuccess);
  };

  onChangeMenuDropdown = (e, { value }) => {
    const { router } = this.props;
    const { push } = router;
    const key = value.split('|')[0];
    const eventId = value.split('|')[1];
    switch (key) {
      case dropdownItemKeys.editEvent:
        push(`/college-events/view-events/edit/${eventId}`);
        break;
      case dropdownItemKeys.publishEventPast:
        push(`/college-events/view-events/publish/${eventId}`);
        break;
      case dropdownItemKeys.publishEvent:
        this.changeEventStatus(eventId, 'PUBLISHED');
        break;
      case dropdownItemKeys.unpublishEvent:
        this.changeEventStatus(eventId, 'UNPUBLISHED');
        break;
      case dropdownItemKeys.deleteEvent:
        this.showDeleteModal(eventId);
        break;
      case dropdownItemKeys.duplicateEvent:
        push(`/college-events/view-events/duplicate/${eventId}`);
        break;
    }
  }

  prepareEventDropdownItems = ({ event, canEdit, canUnpublish, canDelete, canPublish }) => {
    const { intl } = this.props;
    const options = [];

    // edit option item
    if (canEdit) {
      const itemUI = (
        <div
          aria-label={intl.formatMessage(messages.viewEventsEditEvent)}
          tabIndex="-1"
          styleName="button-row"
          data-cy="college-events-dropdown-button-edit"
        >
          <FormattedMessage {...messages.viewEventsEditEvent} />
        </div>
      );
      options.push({
        key: dropdownItemKeys.editEvent,
        text: itemUI,
        accessibility: intl.formatMessage(messages.viewEventsEditEvent),
        value: `${dropdownItemKeys.editEvent}|${event.id}`,
      });
    }

    // publish event item
    if (canPublish && event.past) {
      const itemUI = (
        <div
          aria-label={intl.formatMessage(messages.viewEventsPublishEvent)}
          tabIndex="-1"
          styleName="button-row"
          data-cy="college-events-dropdown-button-publish"
        >
          <FormattedMessage {...messages.viewEventsPublishEvent} />
        </div>
      );
      options.push({
        key: dropdownItemKeys.publishEventPast,
        text: itemUI,
        accessibility: intl.formatMessage(messages.viewEventsPublishEvent),
        value: `${dropdownItemKeys.publishEventPast}|${event.id}`,
      });
    }

    if (canPublish && !event.past) {
      const itemUI = (
        <div
          aria-label={intl.formatMessage(messages.viewEventsPublishEvent)}
          tabIndex="-1"
          styleName="button-row"
          data-cy="college-events-dropdown-button-publish"
        >
          <FormattedMessage {...messages.viewEventsPublishEvent} />
        </div>
      );
      options.push({
        key: dropdownItemKeys.publishEvent,
        text: itemUI,
        accessibility: intl.formatMessage(messages.viewEventsPublishEvent),
        value: `${dropdownItemKeys.publishEvent}|${event.id}`,
      });
    }

    // unpublish event item
    if (canUnpublish) {
      const itemUI = (
        <div
          aria-label={intl.formatMessage(messages.viewEventsUnpublishEvent)}
          tabIndex="-1"
          styleName="button-row"
          data-cy="college-events-dropdown-button-unpublish"
        >
          <FormattedMessage {...messages.viewEventsUnpublishEvent} />
        </div>
      );
      options.push({
        key: dropdownItemKeys.unpublishEvent,
        text: itemUI,
        accessibility: intl.formatMessage(messages.viewEventsUnpublishEvent),
        value: `${dropdownItemKeys.unpublishEvent}|${event.id}`,
      });
    }

    // duplicate event item
    const duplicateItemUI = (
      <div
        aria-label={intl.formatMessage(messages.viewEventsDuplicateEvent)}
        tabIndex="-1"
        styleName="button-row"
        data-cy="college-events-dropdown-button-duplicate"
      >
        <FormattedMessage {...messages.viewEventsDuplicateEvent} />
      </div>
    );
    options.push({
      key: dropdownItemKeys.duplicateEvent,
      text: duplicateItemUI,
      accessibility: intl.formatMessage(messages.viewEventsDuplicateEvent),
      value: `${dropdownItemKeys.duplicateEvent}|${event.id}`,
    });

    // delete event item
    if (canDelete) {
      const itemUI = (
        <div
          aria-label={intl.formatMessage(messages.viewEventsDeleteEvent)}
          tabIndex="-1"
          styleName="button-row"
          data-cy="college-events-dropdown-button-delete"
        >
          <FormattedMessage {...messages.viewEventsDeleteEvent} />
        </div>
      );
      options.push({
        key: dropdownItemKeys.deleteEvent,
        text: itemUI,
        accessibility: intl.formatMessage(messages.viewEventsDeleteEvent),
        value: `${dropdownItemKeys.deleteEvent}|${event.id}`,
      });
    }

    return options;
  }

  EventOptionsMenu = ({ event, canEdit, canUnpublish, canDelete, canPublish }) => (
    <div className="ui right pointing dropdown button">
      <Dropdown
        value={null}
        closeOnEscape
        pointing="top right"
        selectOnBlur={false}
        selectOnNavigation={false}
        icon={null}
        options={this.prepareEventDropdownItems({ event, canEdit, canUnpublish, canDelete, canPublish })}
        onChange={this.onChangeMenuDropdown}
        data-cy="college-events-dropdown"
        trigger={(
          <Icon
            link
            data-cy="college-events-dropdown-button"
            aria-label="Event Options"
            name="ellipsis vertical"
            size="large"
          />
        )}
      />
    </div>
  );

  showDeleteModal = (deletingEventId) => {
    this.setState({ isDeleting: true, deletingEventId });
  }

  showToast = (events, wasStatusChangedOnList, toastTimeout = 5000, toastFadeout = 1000) => {
    const latestUnpublishedEvent = events.filter((event) => event.status === 'UNPUBLISHED')[0] || {};
    const latestPublishedEvent = events.filter((event) => event.status === 'PUBLISHED')[0] || {};
    const latestUnpublishedDate = latestUnpublishedEvent.authoredTime || 0;
    const latestPublishedDate = latestPublishedEvent.authoredTime || 0;
    const threeSeconds = 3 * 1000;
    const unpublishedDateDelta = Date.now() - latestUnpublishedDate;
    const draftToast = unpublishedDateDelta < threeSeconds ? latestUnpublishedEvent.name : false;
    const publishedDateDelta = Date.now() - latestPublishedDate;
    const publishedToast = publishedDateDelta < threeSeconds ? latestPublishedEvent.name : false;
    const unpublishedToast = wasStatusChangedOnList ? draftToast : false;
    this.setState({ unpublishedToast, draftToast, publishedToast, toastFading: false });
    setTimeout(() => {
      const hasNewDraftToast = this.state.draftToast && this.state.draftToast !== draftToast;
      const hasNewPublishedToast = this.state.publishedToast && this.state.publishedToast !== publishedToast;
      if (hasNewDraftToast || hasNewPublishedToast) return;
      this.setState({ toastFading: true });
    }, toastTimeout);
    setTimeout(() => {
      const hasNewDraftToast = this.state.draftToast && this.state.draftToast !== draftToast;
      const hasNewPublishedToast = this.state.publishedToast && this.state.publishedToast !== publishedToast;
      if (hasNewDraftToast || hasNewPublishedToast) return;
      this.setState({ unpublishedToast: false, draftToast: false, publishedToast: false, toastFading: false });
    }, toastTimeout + toastFadeout);
  }

  getToastTitle = () => {
    if (this.state.unpublishedToast) return messages.toastUnpublishedTitle;
    if (this.state.draftToast) return messages.toastDraftedTitle;
    return messages.toastPublishedTitle;
  }

  getToastSubtitle = () => {
    if (this.state.unpublishedToast) return messages.toastUnpublishedSubtitle;
    if (this.state.draftToast) return messages.toastDraftedSubtitle;
    return messages.toastPublishedSubtitle;
  }

  handleExportAttendeesClick = () => {
    this.props.client.query({ query: getEventsAttendeeExportUrl }).then((response) => {
      const url = get(response, 'data.viewer.heAccount.amEventsAttendeeExportUrl', null);
      /* istanbul ignore if */
      if (url) window.location.href = url;
    });
  };

  handleDisplayInChange = (e) => {
    const { target: { id: targetId } } = e;
    const { data } = this.props;
    const { isCaminoChecked, isNavianceChecked } = this.state;
    const camino = targetId.includes('camino') ? e.target.checked : isCaminoChecked;
    const naviancestudent = targetId.includes('naviance') ? e.target.checked : isNavianceChecked;
    const eventsIn = createEventObjectArray({
      rawEvents: data.node.collegeEvents.edges,
      showCamino: camino,
      showNaviance: naviancestudent,
    });

    this.setState({
      isCaminoChecked: camino,
      isNavianceChecked: naviancestudent,
      events: eventsIn.events,
      published: eventsIn.published,
      drafts: eventsIn.drafts,
      expired: eventsIn.expired,
    });
  };

  renderDeleteModal() {
    if (!this.state.isDeleting) return null;
    const title = this.props.intl.formatMessage(messages.deleteModalTitle);
    const subtitle = this.props.intl.formatMessage(messages.deleteModalSubtitle);
    const { deletingEventId } = this.state;
    return (
      <Modal
        closeOnEscape
        onClose={/* istanbul ignore next */() => this.setState({ isDeleting: false })}
        style={{ marginTop: 0 }}
        data-cy="create-event-delete-modal"
        open
        styleName="event-modal"
      >
        <Modal.Content styleName="event-modal-content">
          <div styleName="delete-info" role="alert" aria-label={`${title}, ${subtitle}`}>
            <div styleName="delete-title"><FormattedMessage {...messages.deleteModalTitle} /></div>
            <div styleName="delete-subtitle"><FormattedMessage {...messages.deleteModalSubtitle} /></div>
          </div>
          <div styleName="row border" />
          <div styleName="delete-buttons">
            <Button
              data-cy="create-event-delete-modal-reject-button"
              type="button"
              styleName="reject-delete-button"
              onClick={
                /* istanbul ignore next */
                () => this.setState({
                  isDeleting: false,
                  deletingEventId: null,
                })
              }
            >
              <FormattedMessage {...messages.deleteModalReject} />
            </Button>
            <Button
              data-cy="create-event-delete-modal-confirm-button"
              type="button"
              primary
              onClick={
                /* istanbul ignore next */
                () => this.changeEventStatus(deletingEventId, 'DELETED')
              }
            >
              <FormattedMessage {...messages.deleteModalAction} />
            </Button>
          </div>
        </Modal.Content>
      </Modal>
    );
  }

  renderToast() {
    if (!this.state.draftToast && !this.state.publishedToast) return null;
    const title = this.props.intl.formatMessage(this.getToastTitle());
    const subtitle = this.props.intl.formatMessage(this.getToastSubtitle());
    const eventName = this.state.draftToast || this.state.publishedToast;
    return (
      <div
        styleName={this.state.toastFading ? 'toast-container toast-fadeOut' : 'toast-container toast-fadeIn'}
        data-cy="college-events-toast-container"
      >
        <Icon size="big" name="check circle outline" color="black" />
        <span styleName="toast-title" data-cy="college-events-toast-title">{title}</span>
        <span styleName="toast-subtitle" data-cy="college-events-toast-event-name">{eventName}</span>
        <span styleName="toast-subtitle" data-cy="college-events-toast-subtitle">{subtitle}</span>
      </div>
    );
  }

  renderEmptyState() {
    const { location } = this.props.router;
    const tab = location.pathname.split('/').slice(-1)[0];
    const showButton = tab === 'published' || tab === 'unpublished';
    let title = null;
    let text = null;
    switch (tab) {
      case 'published':
        title = messages.viewEventsEmptyTitlePublished;
        text = messages.viewEventsEmptyTextPublished;
        break;
      case 'unpublished':
        title = messages.viewEventsEmptyTitleUnpublished;
        text = messages.viewEventsEmptyTextUnpublished;
        break;
      case 'expired':
        title = messages.viewEventsEmptyTitleExpired;
        text = messages.viewEventsEmptyTextExpired;
        break;
    }
    return (
      <Grid.Row>
        <Grid.Column width={16} styleName="event-details">
          <Grid width={16} verticalAlign="middle" centered styleName="stacked">
            <Header as="h3" styleName="empty space">
              <FormattedMessage {...title} />
            </Header>
            <div styleName="empty space" data-cy="college-events-midpage-message">
              <FormattedMessage {...text} />
            </div>
            {showButton && (
              <div styleName="space">
                <Button
                  primary
                  data-cy="college-events-midpage-create-event-button"
                  title="Create Event"
                  as={Link}
                  to="/college-events/view-events/create/0"
                >
                  <FormattedMessage {...messages.viewEventsCreateEventNew} />
                </Button>
              </div>
            )}
          </Grid>
        </Grid.Column>
      </Grid.Row>
    );
  }

  renderHeaderButtons() {
    const { fetchAmEventsAttendeeExportAvailable } = this.props;
    let userHasAmEvents = null;
    if (!!fetchAmEventsAttendeeExportAvailable && fetchAmEventsAttendeeExportAvailable.loading === false) {
      userHasAmEvents = fetchAmEventsAttendeeExportAvailable.viewer.heAccount.amEventsAttendeeExportAvailable;
    }
    return (
      <div>
        <Button
          primary
          styleName="create-event"
          title="Create Event"
          data-cy="college-events-create-event-button"
          as={Link}
          to="/college-events/view-events/create/0"
        >
          <FormattedMessage {...messages.viewEventsCreateEvent} />
        </Button>
        {!!userHasAmEvents && (
          <Popup
            content={messages.exportAttendeesTooltipMessage.defaultMessage}
            position="top center"
            size="mini"
            styleName="export-attendees-tooltip"
            trigger={(
              <Button
                styleName="export-attendees"
                title="Create Event"
                data-cy="college-events-export-attendees-button"
                onClick={this.handleExportAttendeesClick}
              >
                <FormattedMessage {...messages.exportAttendees} />
              </Button>
            )}
          />
        )}
      </div>
    );
  }

  renderCheckboxFilters() {
    const { intl } = this.props;
    const { isCaminoChecked, isNavianceChecked } = this.state;
    return (
      <div styleName="checkbox-container" data-cy="college-events-checkbox-container">
        <p><FormattedMessage {...messages.viewEventsHeaderCheckboxes} /></p>
        <Checkbox
          id="checkbox_displayIn_camino"
          name="checkbox_displayIn_camino"
          onChange={this.handleDisplayInChange}
          aria-label={intl.formatMessage(messages.viewEventsCamino)}
          checked={isCaminoChecked}
          label={{
            htmlFor: 'checkbox_displayIn_camino',
            children: (<FormattedMessage {...messages.viewEventsCamino} />),
          }}
        />
        <Checkbox
          id="checkbox_displayIn_naviance"
          name="checkbox_displayIn_naviance"
          onChange={this.handleDisplayInChange}
          aria-label={intl.formatMessage(messages.viewEventsNaviance)}
          checked={isNavianceChecked}
          label={{
            htmlFor: 'checkbox_displayIn_naviance',
            children: (<FormattedMessage {...messages.viewEventsNaviance} />),
          }}
        />
      </div>
    );
  }

  render() {
    const {
      events,
      published,
      drafts,
      expired,
      displayInCheckboxes,
    } = this.state;

    const { location } = this.props.router;
    const { intl, enabledFeatures, heAccount } = this.props;
    const tab = location.pathname.split('/').slice(-1)[0];
    const noEventsInTab = !hasEventsInTab(tab, published, drafts, expired);
    const activeSubscriptions = get(heAccount, 'activeSubscriptions', []);

    return (
      <Item
        styleName="container"
      >
        {this.renderToast()}
        {this.renderDeleteModal()}
        <PageTitle subTitle="View Events" title="Events" category="Intersect" />
        <div styleName="events-header-container">
          <Header as="h1" color="black" size="large" id="pageTitle">
            <FormattedMessage {...messages.viewEventsHeader} />
          </Header>
          {displayInCheckboxes && this.renderHeaderButtons()}
        </div>
        <div styleName="events-nav-container">
          <nav aria-label="Events Sub Menu">
            <Menu styleName="navigation-menu" stackable as="ul" pointing secondary size="huge">
              <Menu.Item as="li" link>
                <Link
                  styleName="menu-link"
                  data-cy="college-events-published-link"
                  to="/college-events/view-events/published"
                  activeClassName={active}
                  aria-label={intl.formatMessage(messages.viewEventsPublished, { published })}
                >
                  <FormattedMessage id="collegeEvents.view.events" {...messages.viewEventsPublished} values={{ published }} />
                </Link>
              </Menu.Item>
              <Menu.Item as="li" link>
                <Link
                  styleName="menu-link"
                  data-cy="college-events-unpublished-link"
                  to="/college-events/view-events/unpublished"
                  activeClassName={active}
                  aria-label={intl.formatMessage(messages.viewEventsUnpublished, { drafts })}
                >
                  <FormattedMessage id="collegeEvents.view.events" {...messages.viewEventsUnpublished} values={{ drafts }} />
                </Link>
              </Menu.Item>
              <Menu.Item as="li" link>
                <Link
                  styleName="menu-link"
                  data-cy="college-events-expired-link"
                  to="/college-events/view-events/expired"
                  activeClassName={active}
                  aria-label={intl.formatMessage(messages.viewEventsExpired, { expired })}
                >
                  <FormattedMessage id="collegeEvents.view.events" {...messages.viewEventsExpired} values={{ expired }} />
                </Link>
              </Menu.Item>
            </Menu>
          </nav>
          {!displayInCheckboxes && this.renderHeaderButtons()}
          {displayInCheckboxes && this.renderCheckboxFilters()}
        </div>
        <Grid stackable width={16} verticalAlign="middle" styleName="content" id="content">
          {noEventsInTab && this.renderEmptyState()}
          {!noEventsInTab && events.map((event) => (
            event.category === tab && (
              // eslint-disable-next-line max-len
              <Grid.Row key={event.id} data-cy="college-events-row-for-event" styleName={event.category === tab ? 'row-show' : /* istanbul ignore next: implement test with mutation */'row-hide'}>
                <Grid.Column width={8} styleName="event-details">
                  <Grid width={16} verticalAlign="middle">
                    <Link
                      to={`/college-events/view-events/edit/${event.id}`}
                    >
                      <Header as="h3" styleName="event-name" data-cy="college-events-event-name">
                        {event.name}
                      </Header>
                    </Link>
                  </Grid>
                  <Grid width={16} verticalAlign="middle">
                    <div styleName="event-info">
                      <div styleName="date nowrap" data-cy="college-events-event-date">{`${event.displayDate}${event.endDate}`}</div>
                      <div styleName="time nowrap" data-cy="college-events-event-time">{event.startTime}</div>
                      <div styleName="location nowrap" data-cy="college-events-event-location">{
                        event.virtual ?
                          <FormattedMessage {...messages.locationVirtualEvent} /> :
                          `${event.location}, ${event.state}`
                      }
                      </div>
                    </div>
                  </Grid>
                </Grid.Column>
                <Grid.Column width={4} styleName="event-sub-info" data-cy="college-events-event-status">
                  <div>
                    <div styleName={`dot ${event.category}`} />{capitalize(event.category)}
                    {getDisplayInMessage({
                      event,
                      enabledFeatures,
                      activeSubscriptions,
                      messages,
                      intl,
                    })}
                  </div>
                </Grid.Column>
                <Grid.Column width={4} styleName="options">
                  <Grid width={16} verticalAlign="middle">
                    <Grid.Column
                      data-cy="college-events-event-timestamp"
                      width={13}
                      styleName="close-col left"
                    >
                      {event.authored}
                    </Grid.Column>
                    <Grid.Column width={1} styleName="close-col left">
                      {this.EventOptionsMenu({
                        event,
                        canEdit: event.category !== 'expired' || /* istanbul ignore next: implement test with mutation */false,
                        canUnpublish: event.category === 'published' || /* istanbul ignore next: implement test with mutation */false,
                        canDelete: ['unpublished', 'published'].includes(event.category) || /* istanbul ignore next */false,
                        canPublish: event.category === 'unpublished' || false,
                      })}
                    </Grid.Column>
                  </Grid>
                </Grid.Column>
                <Grid.Column width={16}>
                  <Divider styleName="event-divider" />
                </Grid.Column>
              </Grid.Row>
            )
          ))}
        </Grid>
      </Item>
    );
  }
}

export const CollegeEventsViewEventsComponentWithIntl = injectIntl(CollegeEventsViewEventsComponent);
export default withRouter(withApollo(CollegeEventsViewEventsComponentWithIntl));
